import type { Lang } from '../../i18n/context'
import { JsonReader } from '../jsonReader'
import type { JsonObject } from '../jsonReader'

/** A draft is published for review but not yet in force; an in-force document carries the date it took effect. */
export type LegalStatus = 'draft' | 'inForce'

export interface LegalSection {
  readonly id: string
  readonly heading: string
  readonly paragraphs: readonly string[]
}

export interface LegalContent {
  readonly title: string
  readonly intro: string
  readonly sections: readonly LegalSection[]
}

export interface LegalDocument {
  readonly version: string
  readonly status: LegalStatus
  readonly effectiveDate: string | null
  readonly content: Readonly<Record<Lang, LegalContent>>
}

const LEGAL_STATUSES: readonly LegalStatus[] = ['draft', 'inForce']
const ISO_DATE_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}$/
// Section ids double as URL fragments (/terms#disclaimer), so apps can deep-link a section.
const SECTION_ID_PATTERN: RegExp = /^[a-z][a-z0-9-]*$/

function readStatus(reader: JsonReader, value: unknown): LegalStatus {
  const status: LegalStatus | undefined = LEGAL_STATUSES.find((s) => s === value)
  if (status === undefined) reader.fail('status', `must be one of ${LEGAL_STATUSES.join(', ')}, got ${JSON.stringify(value)}`)
  return status
}

function readEffectiveDate(reader: JsonReader, status: LegalStatus, value: unknown): string | null {
  if (status === 'draft') {
    if (value !== null) reader.fail('effectiveDate', `must be null while status is draft, got ${JSON.stringify(value)}`)
    return null
  }
  if (typeof value !== 'string' || !ISO_DATE_PATTERN.test(value) || Number.isNaN(Date.parse(value))) reader.fail('effectiveDate', `must be an ISO date (YYYY-MM-DD) when status is inForce, got ${JSON.stringify(value)}`)
  return value
}

function readSection(reader: JsonReader, value: unknown, path: string): LegalSection {
  const raw: JsonObject = reader.object(value, path)
  const id: string = reader.string(raw.id, `${path}.id`)
  if (!SECTION_ID_PATTERN.test(id)) reader.fail(`${path}.id`, `must be lowercase kebab-case (it is used as a URL fragment), got ${JSON.stringify(id)}`)
  const paragraphs: readonly string[] = reader.array(raw.paragraphs, `${path}.paragraphs`).map((p, i) => reader.string(p, `${path}.paragraphs[${i}]`))
  return { id, heading: reader.string(raw.heading, `${path}.heading`), paragraphs }
}

function readContent(reader: JsonReader, content: JsonObject, lang: Lang): LegalContent {
  const path: string = `content.${lang}`
  const raw: JsonObject = reader.object(content[lang], path)
  const sections: readonly LegalSection[] = reader.array(raw.sections, `${path}.sections`).map((s, i) => readSection(reader, s, `${path}.sections[${i}]`))
  const duplicate: LegalSection | undefined = sections.find((s, i) => sections.findIndex((other) => other.id === s.id) !== i)
  if (duplicate) reader.fail(`${path}.sections`, `has duplicate section id ${JSON.stringify(duplicate.id)}`)
  return { title: reader.string(raw.title, `${path}.title`), intro: reader.string(raw.intro, `${path}.intro`), sections }
}

// Every language must carry the same sections in the same order, so a translation cannot silently drift from the original.
function assertSameSections(reader: JsonReader, content: Readonly<Record<Lang, LegalContent>>): void {
  const [reference, ...others]: [Lang, LegalContent][] = Object.entries(content) as [Lang, LegalContent][]
  const referenceIds: string = reference[1].sections.map((s) => s.id).join(', ')
  for (const [lang, other] of others) {
    const ids: string = other.sections.map((s) => s.id).join(', ')
    if (ids !== referenceIds) reader.fail(`content.${lang}.sections`, `must match content.${reference[0]}.sections id for id; ${reference[0]} has [${referenceIds}], ${lang} has [${ids}]`)
  }
}

/** Validates a legal document JSON file at the point it enters the app; throws with the file and field path on the first problem. */
export function parseLegalDocument(value: unknown, source: string): LegalDocument {
  const reader: JsonReader = new JsonReader(`legal document ${source}`)
  const raw: JsonObject = reader.object(value, '(root)')
  const status: LegalStatus = readStatus(reader, raw.status)
  const contentRaw: JsonObject = reader.object(raw.content, 'content')
  const content: Readonly<Record<Lang, LegalContent>> = { no: readContent(reader, contentRaw, 'no'), en: readContent(reader, contentRaw, 'en') }
  assertSameSections(reader, content)
  return { version: reader.string(raw.version, 'version'), status, effectiveDate: readEffectiveDate(reader, status, raw.effectiveDate), content }
}
